import { useEffect, useState } from "react";

import Login from "./components/Login";
import Layout from "./components/Layout";
import Dashboard from "./components/Dashboard";
import WareneingangRetoure from "./components/WareneingangRetoure";
import BioWareneingang from "./components/BioWareneingang";
import Mehraufwand from "./components/Mehraufwand";
import TariffCalculatorPage from "./components/TariffCalculatorPage";
import TariffImportPage from "./components/TariffImportPage";
import TariffManagementPage from "./components/TariffManagementPage";
import SurchargeManagementPage from "./components/SurchargeManagementPage";
import AuditLog from "./components/AuditLog";
import Benutzerverwaltung from "./components/Benutzerverwaltung";
import Lieferantenverwaltung from "./components/Lieferantenverwaltung";
import Abrechnung from "./components/Abrechnung";
import Abrechnungsdaten from "./components/Abrechnungsdaten";
import Rechnungen from "./components/Rechnungen";
import { createAutomaticDamageEffort } from "./utils/damageRecording";
const pagePaths = {
  dashboard: "/",
  wareneingang: "/wareneingang",
  warenausgang: "/warenausgang",
  "bio-we": "/bio-we",
  "traces-we": "/traces-we",
  mehraufwand: "/mehraufwand",
  tarifrechner: "/tarifrechner",
  tarifimport: "/admin/tarifimport",
  tarife: "/admin/tarife",
  zuschlaege: "/admin/zuschlaege",
  abrechnung: "/admin/abrechnung",
  rechnungen: "/admin/rechnungen",
  abrechnungsdaten: "/admin/abrechnungsdaten",
  lieferanten: "/lieferanten",
  benutzer: "/benutzer",
  protokoll: "/protokoll",
};

const pathPages = Object.fromEntries(
  Object.entries(pagePaths).map(([page, path]) => [path, page])
);

function getPageFromPath() {
  if (typeof window === "undefined") {
    return "dashboard";
  }

  return pathPages[window.location.pathname] || "dashboard";
}

async function apiRequest(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });

  const contentType = response.headers.get("content-type") || "";
  const data = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const error = new Error(
      typeof data === "string" && data.trim()
        ? response.status === 413
          ? "Das aufgenommene Bild ist zu groß. Bitte versuche es mit einem kleineren Foto noch einmal."
          : data.trim()
        : data?.error || "Die Anfrage konnte nicht verarbeitet werden."
    );
    error.status = response.status;
    throw error;
  }

  return data;
}

export default function App() {
  const getIsDesktop = () =>
    typeof window === "undefined" ? true : window.innerWidth > 860;
  const [user, setUser] = useState(null);
  const [activePage, setActivePage] = useState(getPageFromPath);
  const [isDesktop, setIsDesktop] = useState(getIsDesktop);
  const [records, setRecords] = useState([]);
  const [outgoingRecords, setOutgoingRecords] = useState([]);
  const [bioRecords, setBioRecords] = useState([]);
  const [extraEfforts, setExtraEfforts] = useState([]);
  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [backups, setBackups] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    initializeSession();
    // Nur einmal beim App-Start die serverseitige Sitzung prüfen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => setIsDesktop(getIsDesktop());

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    const handlePopState = () => setActivePage(getPageFromPath());

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const nextPath = pagePaths[activePage] || "/";

    if (window.location.pathname !== nextPath) {
      window.history.pushState({}, "", nextPath);
    }
  }, [activePage]);

  useEffect(() => {
    if (user && (!user.isAdmin || !isDesktop) && activePage === "dashboard") {
      setActivePage("wareneingang");
    }

    if (!user?.isAdmin && activePage === "bio-we" && !user?.bioApproval) {
      setActivePage("wareneingang");
    }

    if (!user?.isAdmin && activePage === "traces-we" && !user?.tracesApproval) {
      setActivePage("wareneingang");
    }

    if (!user?.isAdmin && activePage === "tarifimport") {
      setActivePage("wareneingang");
    }
  }, [activePage, isDesktop, user]);

  const initializeSession = async () => {
    try {
      setIsLoading(true);
      setLoadError("");
      const currentUser = await apiRequest("/api/me");
      setUser(currentUser);
      await loadAppData();
    } catch (error) {
      if (error.status === 401) {
        setUser(null);
        setLoadError("");
      } else {
        setLoadError(error.message);
      }
      setIsLoading(false);
    }
  };

  const loadAppData = async () => {
    try {
      setIsLoading(true);
      setLoadError("");

      const data = await apiRequest("/api/bootstrap");
      setRecords(data.records || []);
      setOutgoingRecords(data.outgoingRecords || []);
      setBioRecords(data.bioRecords || []);
      setExtraEfforts(data.extraEfforts || []);
      setUsers(data.users || []);
      setSuppliers(data.suppliers || []);
      setAuditLogs(data.auditLogs || []);
      setBackups(data.backups || []);
      setInvoices(data.invoices || []);

    } catch (error) {
      setLoadError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (username, password) => {
    const loggedInUser = await apiRequest("/api/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });

    setUser(loggedInUser);
    await loadAppData();
  };

  const handleLogout = async () => {
    try {
      await apiRequest("/api/logout", { method: "POST" });
    } catch (error) {
      // Lokalen Zustand trotzdem beenden, falls die Sitzung serverseitig bereits abgelaufen ist.
    } finally {
      setUser(null);
      setRecords([]);
      setOutgoingRecords([]);
      setBioRecords([]);
      setExtraEfforts([]);
      setUsers([]);
      setSuppliers([]);
      setAuditLogs([]);
      setBackups([]);
      setInvoices([]);
    }
  };

  const refreshAuditLogs = async () => {
    if (!user?.isAdmin) {
      return;
    }

    const logs = await apiRequest("/api/audit-logs");
    setAuditLogs(logs || []);
  };

  const saveUser = async (userPayload) => {
    const savedUser = await apiRequest("/api/users/save", {
      method: "POST",
      body: JSON.stringify(userPayload),
    });

    setUsers((currentUsers) => {
      const exists = currentUsers.some((item) => item.id === savedUser.id);
      const nextUsers = exists
        ? currentUsers.map((item) =>
            item.id === savedUser.id ? savedUser : item
          )
        : [...currentUsers, savedUser];

      if (user && user.id === savedUser.id) {
        setUser(savedUser);
      }

      return nextUsers;
    });
    await refreshAuditLogs();
  };

  const deleteUser = async (userId) => {
    await apiRequest(`/api/users/${userId}`, {
      method: "DELETE",
    });

    setUsers((currentUsers) => currentUsers.filter((item) => item.id !== userId));

    if (user?.id === userId) {
      handleLogout();
    }
    await refreshAuditLogs();
  };

  const saveSupplier = async (supplierPayload) => {
    const savedSupplier = await apiRequest("/api/suppliers/save", {
      method: "POST",
      body: JSON.stringify(supplierPayload),
    });

    setSuppliers((currentSuppliers) => {
      const exists = currentSuppliers.some(
        (item) => item.id === savedSupplier.id
      );
      return exists
        ? currentSuppliers.map((item) =>
            item.id === savedSupplier.id ? savedSupplier : item
          )
          : [...currentSuppliers, savedSupplier];
    });
    await refreshAuditLogs();
    return savedSupplier;
  };

  const deleteSupplier = async (supplierId) => {
    await apiRequest(`/api/suppliers/${supplierId}`, {
      method: "DELETE",
    });

    setSuppliers((currentSuppliers) =>
      currentSuppliers.filter((item) => item.id !== supplierId)
    );
    await refreshAuditLogs();
  };

  const saveRecord = async (recordPayload) => {
    const savedRecord = await apiRequest("/api/records/save", {
      method: "POST",
      body: JSON.stringify(recordPayload),
    });

    setRecords((currentRecords) => [...currentRecords, savedRecord]);

    if (recordPayload.damageReport === "Ja") {
      const savedDamageEffort = await apiRequest("/api/extra-efforts/save", {
        method: "POST",
        body: JSON.stringify(
          createAutomaticDamageEffort(recordPayload, user?.username)
        ),
      });

      setExtraEfforts((currentEfforts) => [...currentEfforts, savedDamageEffort]);
    }

    await refreshAuditLogs();
  };

  const deleteRecord = async (recordId) => {
    await apiRequest(`/api/records/${recordId}`, {
      method: "DELETE",
    });

    setRecords((currentRecords) =>
      currentRecords.filter((item) => item.id !== recordId)
    );
    await refreshAuditLogs();
  };

  const toggleRecordProcessed = async (recordId, processed) => {
    const updatedRecord = await apiRequest(`/api/records/${recordId}/processed`, {
      method: "PATCH",
      body: JSON.stringify({ processed }),
    });

    setRecords((currentRecords) =>
      currentRecords.map((item) => (item.id === recordId ? updatedRecord : item))
    );
  };

  const saveOutgoingRecord = async (recordPayload) => {
    const savedRecord = await apiRequest("/api/outgoing-records/save", {
      method: "POST",
      body: JSON.stringify(recordPayload),
    });

    setOutgoingRecords((currentRecords) => [...currentRecords, savedRecord]);

    await refreshAuditLogs();
  };

  const deleteOutgoingRecord = async (recordId) => {
    await apiRequest(`/api/outgoing-records/${recordId}`, {
      method: "DELETE",
    });

    setOutgoingRecords((currentRecords) =>
      currentRecords.filter((item) => item.id !== recordId)
    );
    await refreshAuditLogs();
  };

  const toggleOutgoingRecordProcessed = async (recordId, processed) => {
    const updatedRecord = await apiRequest(
      `/api/outgoing-records/${recordId}/processed`,
      {
        method: "PATCH",
        body: JSON.stringify({ processed }),
      }
    );

    setOutgoingRecords((currentRecords) =>
      currentRecords.map((item) => (item.id === recordId ? updatedRecord : item))
    );
  };

  const addOutgoingRecordAbd = async (recordId, abdDataList) => {
    const updatedRecord = await apiRequest(`/api/outgoing-records/${recordId}/abd`, {
      method: "PATCH",
      body: JSON.stringify({ abdDataList }),
    });

    setOutgoingRecords((currentRecords) =>
      currentRecords.map((item) => (item.id === recordId ? updatedRecord : item))
    );
  };

  const saveBioRecord = async (recordPayload) => {
    const savedRecord = await apiRequest("/api/bio-records/save", {
      method: "POST",
      body: JSON.stringify(recordPayload),
    });

    setBioRecords((currentRecords) => [...currentRecords, savedRecord]);
    await refreshAuditLogs();
  };

  const deleteBioRecord = async (recordId) => {
    await apiRequest(`/api/bio-records/${recordId}`, {
      method: "DELETE",
    });

    setBioRecords((currentRecords) =>
      currentRecords.filter((item) => item.id !== recordId)
    );
    await refreshAuditLogs();
  };

  const toggleBioRecordProcessed = async (recordId, processed) => {
    const updatedRecord = await apiRequest(
      `/api/bio-records/${recordId}/processed`,
      {
        method: "PATCH",
        body: JSON.stringify({ processed }),
      }
    );

    setBioRecords((currentRecords) =>
      currentRecords.map((item) => (item.id === recordId ? updatedRecord : item))
    );
  };

  const saveExtraEffort = async (effortPayload) => {
    const savedEffort = await apiRequest("/api/extra-efforts/save", {
      method: "POST",
      body: JSON.stringify(effortPayload),
    });

    setExtraEfforts((currentEfforts) => [...currentEfforts, savedEffort]);
    await refreshAuditLogs();
  };

  const deleteExtraEffort = async (effortId) => {
    await apiRequest(`/api/extra-efforts/${effortId}`, {
      method: "DELETE",
    });

    setExtraEfforts((currentEfforts) =>
      currentEfforts.filter((item) => item.id !== effortId)
    );
    await refreshAuditLogs();
  };

  const toggleExtraEffortProcessed = async (effortId, processed) => {
    const updatedEffort = await apiRequest(
      `/api/extra-efforts/${effortId}/processed`,
      {
        method: "PATCH",
        body: JSON.stringify({ processed }),
      }
    );

    setExtraEfforts((currentEfforts) =>
      currentEfforts.map((item) => (item.id === effortId ? updatedEffort : item))
    );
  };

  const updateExtraEffortBilling = async (effortId, billingPayload) => {
    const updatedEffort = await apiRequest(`/api/extra-efforts/${effortId}/billing`, {
      method: "PATCH",
      body: JSON.stringify(billingPayload),
    });

    setExtraEfforts((currentEfforts) =>
      currentEfforts.map((item) => (item.id === effortId ? updatedEffort : item))
    );
  };

  const createBackup = async () => {
    const backup = await apiRequest("/api/backups/create", {
      method: "POST",
    });

    setBackups((currentBackups) => [backup, ...currentBackups].slice(0, 1));
    await refreshAuditLogs();
    return backup;
  };

  const saveInvoice = async (invoicePayload) => {
    const savedInvoice = await apiRequest("/api/invoices/save", {
      method: "POST",
      body: JSON.stringify(invoicePayload),
    });

    setInvoices((currentInvoices) => {
      const exists = currentInvoices.some((invoice) => invoice.id === savedInvoice.id);
      return exists
        ? currentInvoices.map((invoice) => invoice.id === savedInvoice.id ? savedInvoice : invoice)
        : [savedInvoice, ...currentInvoices];
    });
    return savedInvoice;
  };

  const saveSupplierBillingAgreement = async (supplierId, billingAgreement) => {
    await apiRequest(`/api/suppliers/${supplierId}/billing-agreement`, {
      method: "PATCH",
      body: JSON.stringify({ billingAgreement }),
    });

    setSuppliers((currentSuppliers) =>
      currentSuppliers.map((supplier) =>
        supplier.id === supplierId ? { ...supplier, billingAgreement } : supplier
      )
    );
  };

  const markBillingRowProcessed = async (row) => {
    if (row.source === "records") {
      await toggleRecordProcessed(row.id, true);
    } else if (row.source === "outgoingRecords") {
      await toggleOutgoingRecordProcessed(row.id, true);
    } else if (row.source === "bioRecords") {
      await toggleBioRecordProcessed(row.id, true);
    } else if (row.source === "extraEfforts") {
      await toggleExtraEffortProcessed(row.id, true);
    }
  };

  if (isLoading) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <h1>ZENTRAQ wird geladen</h1>
          <p className="section-text">
            Die Verbindung zur lokalen Datenbank wird vorbereitet.
          </p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <h1>Verbindung fehlgeschlagen</h1>
          <div className="message message-error">{loadError}</div>
          <div className="button-row">
            <button onClick={loadAppData} className="button button-dark">
              Erneut versuchen
            </button>
          </div>
          <div className="message message-info">
            Falls die Meldung bleibt, starte bitte die Datei{" "}
            <strong>start-zentraq.bat</strong> per Doppelklick.
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <Layout
      user={user}
      onLogout={handleLogout}
      activePage={activePage}
      setActivePage={setActivePage}
      isDesktop={isDesktop}
    >
      {activePage === "dashboard" && user.isAdmin && isDesktop && (
        <Dashboard
          records={records}
          outgoingRecords={outgoingRecords}
          bioRecords={bioRecords}
          extraEfforts={extraEfforts}
          suppliers={suppliers}
          user={user}
          backups={backups}
          onCreateBackup={createBackup}
        />
      )}

      {activePage === "wareneingang" && (
        <WareneingangRetoure
          key="wareneingang"
          records={records}
          suppliers={suppliers}
          user={user}
          onSaveRecord={saveRecord}
          onDeleteRecord={deleteRecord}
          onToggleProcessed={toggleRecordProcessed}
        />
      )}

      {activePage === "warenausgang" && (
        <WareneingangRetoure
          key="warenausgang"
          pageTitle="Warenausgang"
          pageDescription="Die Schnell-Erfassung fuer Warenausgang nutzt den gleichen Ablauf wie der Wareneingang."
          recordTypeOptions={["Warenausgang"]}
          defaultType="Warenausgang"
          listTitle="Gespeicherte Warenausgaenge"
          records={outgoingRecords}
          suppliers={suppliers}
          user={user}
          onSaveRecord={saveOutgoingRecord}
          onDeleteRecord={deleteOutgoingRecord}
          onToggleProcessed={toggleOutgoingRecordProcessed}
          onAddAbdToRecord={addOutgoingRecordAbd}
          showAbdColumn
        />
      )}

      {activePage === "bio-we" && (user.isAdmin || user.bioApproval) && (
        <BioWareneingang
          records={bioRecords}
          suppliers={suppliers}
          user={user}
          onSaveRecord={saveBioRecord}
          onDeleteRecord={deleteBioRecord}
          onToggleProcessed={toggleBioRecordProcessed}
        />
      )}

      {activePage === "traces-we" && (user.isAdmin || user.tracesApproval) && (
        <div className="page-stack">
          <div>
            <p className="eyebrow">Erfassung</p>
            <h1 className="page-title">Traces WE</h1>
            <p className="section-text">
              Der Ablauf fuer Traces WE ist noch nicht definiert.
            </p>
          </div>
        </div>
      )}

      {activePage === "mehraufwand" && (
        <Mehraufwand
          efforts={extraEfforts}
          suppliers={suppliers}
          user={user}
          onSaveEffort={saveExtraEffort}
          onDeleteEffort={deleteExtraEffort}
          onToggleProcessed={toggleExtraEffortProcessed}
          onUpdateBilling={updateExtraEffortBilling}
        />
      )}


      {activePage === "tarifrechner" && (
        <TariffCalculatorPage suppliers={suppliers} user={user} />
      )}

      {activePage === "tarife" && user.isAdmin && (
        <TariffManagementPage />
      )}

      {activePage === "tarifimport" && user.isAdmin && (
        <TariffImportPage user={user} />
      )}

      {activePage === "zuschlaege" && user.isAdmin && (
        <SurchargeManagementPage />
      )}

      {activePage === "abrechnung" && user.isAdmin && (
        <Abrechnung
          records={records}
          outgoingRecords={outgoingRecords}
          bioRecords={bioRecords}
          extraEfforts={extraEfforts}
          suppliers={suppliers}
          invoices={invoices}
          onMarkProcessed={markBillingRowProcessed}
          onSaveInvoice={saveInvoice}
          onSaveAgreement={saveSupplierBillingAgreement}
        />
      )}

      {activePage === "rechnungen" && user.isAdmin && (
        <Rechnungen invoices={invoices} suppliers={suppliers} />
      )}

      {activePage === "abrechnungsdaten" && user.isAdmin && (
        <Abrechnungsdaten />
      )}

      {activePage === "lieferanten" && user.isAdmin && (
        <Lieferantenverwaltung
          suppliers={suppliers}
          onSaveSupplier={saveSupplier}
          onDeleteSupplier={deleteSupplier}
        />
      )}

      {activePage === "benutzer" && user.isAdmin && (
        <Benutzerverwaltung
          users={users}
          onSaveUser={saveUser}
          onDeleteUser={deleteUser}
        />
      )}

      {activePage === "protokoll" && user.isAdmin && (
        <AuditLog logs={auditLogs} />
      )}
    </Layout>
  );
}







